import { useContext, useEffect, useMemo, useReducer, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { WppBreadcrumb, WppTypography } from '@wppopen/components-library-react'
import TableMetaDataRow from 'components/table/TableMetaDataRow'
import Table from 'components/table'
import clsx from 'clsx'
import {
  getPaginatedTableReducerEmptyState,
  paginatedTableReducer,
  setRowData,
  setTaskPosition,
  setTotalItemCount,
} from 'reducers/PaginatedTableReducer/PaginatedTableReducer'
import Banner from 'components/Banner'
import Filters from 'components/Filters/Filters'
import {
  filterReducer,
  getFilterReducerEmptyState,
  initialiseFilterOptions,
} from 'reducers/GenericFiltersReducer/GenericFiltersReducer'
import PageHeader from 'components/PageHeader/PageHeader'
import useInitialiser from 'hooks/useInitialiser'
import { getFilterElementInitFields } from 'pages/Home/utils'
import { getTypeKey } from 'utils'
import { DEFAULT_API_ITEM_LIMITS, FILTER_IDS } from 'utils/constants'
import {
  columnStructure,
  RESET_TABLE_POSITION,
  PROJECT_REGION_IDS,
  STATUSES,
  projectImportLogsFilterData,
  ErrorPortal,
} from './utils'
import ProjectPageContext from 'context/projectContext/projectContext'
import NotFound404Page from 'pages/NotFound404Page/NotFound404Page'
import { handleLoadMore, handleSortingUpdate, refreshHandler } from '../utils'
import useApi from 'hooks/useApi'
import useToast from 'hooks/useToast'
import type { RowClickedEvent } from 'ag-grid-community'
import type { WppBreadcrumbCustomEvent } from '@wppopen/components-library/dist/types/components'
import type { BreadcrumbItemEventDetails } from '@wppopen/components-library'
import type { ImportsCreateDataV5, ImportsListDataV5 } from 'utils/utils.d'
import type { ImportsDownloadDetailData } from '@contracts'
import type { IdValuePair } from 'reducers/GenericFiltersReducer/GenericFiltersReducer.d'
import styles from './ImportedLogsView.module.scss'

const { IMPORT_LOGS_API_ITEM_LIMITS } = DEFAULT_API_ITEM_LIMITS

export default function ImportedLogsView() {
  const { project_id } = useParams()
  const { singleProjectApiDetails, brandDetails, clientDetails } = useContext(ProjectPageContext)

  const navigate = useNavigate()
  const [portalContainer, setPortalContainer] = useState<{
    rowElement: Element
    topPosition: boolean
    errors: string[]
    selectedImportId: string
  }>()
  const { toastDetails, setToastDetails } = useToast()
  // get project details
  const {
    data: singleProjectData,
    isLoading: isSingleProjectLoading,
    error: singleProjectError,
  } = singleProjectApiDetails

  // api call to get details of imports
  const importLogsApiDetails = useApi<ImportsListDataV5>(
    `products/imports?project_id=${project_id}&position=0&limit=${IMPORT_LOGS_API_ITEM_LIMITS}`,
  )

  const {
    fetchData: importLogsFetch,
    data: importLogsData,
    isLoading: isImportLogsLoading,
    url: importLogsUrl,
    setUrl: setImportLogsUrl,
    error: importLogsError,
  } = importLogsApiDetails

  const { project_name, client_id, brand_id } = singleProjectData || {}
  const breadCrumbItems = useMemo(
    () => [
      {
        label: 'Projects',
        path: '/',
      },
      {
        label: project_name || 'Project',
        path: `/project/${project_id}`,
      },
      {
        label: 'Import Log',
        path: `/project/${project_id}/logs`,
      },
    ],
    [project_name],
  )
  const [shouldAppend, setShouldAppend] = useState(false)
  const [filtersReducerState, filtersReducerDispatch] = useReducer(
    filterReducer,
    getFilterReducerEmptyState(FILTER_IDS.PROJECT_IMPORT_LOGS, projectImportLogsFilterData, '', true, true),
  )
  const filterIndex = filtersReducerState.index
  const region_ids = filterIndex[PROJECT_REGION_IDS]
  const statuses = filterIndex[STATUSES]
  const regionActiveFilters = region_ids.activeFilters
  const statusActiveFilters = statuses.activeFilters
  const [isTableFirstLoad, setIsTableFirstLoad] = useState(true)
  const [importsTableReducerState, importsTableReducerDispatch] = useReducer(
    paginatedTableReducer,
    getPaginatedTableReducerEmptyState(),
  )
  const { isInitialising } = useInitialiser({ subscribedApis: [singleProjectApiDetails, importLogsApiDetails] })

  // api to download import logs
  const { fetchData: downloadImportLogs } = useApi<ImportsDownloadDetailData>('')

  // handle breadcrumb click
  const handleRouteChange = (e: WppBreadcrumbCustomEvent<BreadcrumbItemEventDetails>) => {
    if (isInitialising) return
    const { path } = e.detail
    navigate(path)
  }

  // show error banner and remove it
  useEffect(() => {
    if (importLogsError) {
      setIsTableFirstLoad(false)
      setToastDetails({
        type: 'error',
        message: importLogsError.message || importLogsError.errors?.[0].message || 'An error occurred',
      })
    } else {
      setToastDetails(undefined)
    }
  }, [importLogsError])

  // fetch data every time url changes
  useEffect(() => {
    importLogsFetch(importLogsUrl)
      .then(() => {
        setIsTableFirstLoad(false)
      })
      .catch(console.error)
  }, [importLogsUrl])

  // update table reducer state
  useEffect(() => {
    if (isImportLogsLoading || !importLogsData || importLogsError) return

    importsTableReducerDispatch(setRowData({ rowData: importLogsData.items || [], shouldAppend: shouldAppend }))
    importsTableReducerDispatch(
      setTaskPosition(shouldAppend ? (importLogsData.position || 0) + importLogsData.item_count! : 0),
    )
    importsTableReducerDispatch(setTotalItemCount(importLogsData.total_item_count!))
    setShouldAppend(false)
  }, [isImportLogsLoading])

  // update region filter state
  useEffect(() => {
    const regions = singleProjectData?.regions
    if (!regions) return

    // extract region data from the project data and create the region filter ELEMENTs
    const buildRegionsDetailsForFilter = regions.map(
      ({ _id, region_name }) =>
        ({
          id: _id,
          value: region_name,
        }) as IdValuePair,
    )

    // Get the data for initialising the region filter element
    const { currentFilterComponent: regionFilterComponent } = getFilterElementInitFields<IdValuePair>(
      getTypeKey<IdValuePair>('value'),
      buildRegionsDetailsForFilter,
      null,
    )

    // update the filter state with the api option data
    filtersReducerDispatch(initialiseFilterOptions(PROJECT_REGION_IDS, regionFilterComponent))
  }, [singleProjectData?.regions])

  // update status filter state
  useEffect(() => {
    const newURL = new URL(importLogsUrl)

    // Get region and status IDs from active filters
    const regionIds = regionActiveFilters.map(region => (region as IdValuePair).id).join(',')
    const statusIds = statusActiveFilters.map(status => (status as IdValuePair).id).join(',')

    // Reset position in search params
    newURL.searchParams.set('position', RESET_TABLE_POSITION.toString())

    // Update or delete project region ID and status in search params based on their existence
    if (!!regionIds && !!regionActiveFilters) newURL.searchParams.set(PROJECT_REGION_IDS, regionIds)
    else newURL.searchParams.delete(PROJECT_REGION_IDS)

    if (!!statusIds && !!statusActiveFilters) newURL.searchParams.set(STATUSES, statusIds)
    else newURL.searchParams.delete(STATUSES)

    // Retrieve current project region ID and status for comparison
    const getOldProjectRegionId = importLogsUrl.searchParams.get(PROJECT_REGION_IDS) || ''
    const getOldStatus = importLogsUrl.searchParams.get(STATUSES) || ''

    // If region or status IDs have changed, update the URL
    if (regionIds !== getOldProjectRegionId || statusIds !== getOldStatus) {
      setImportLogsUrl(newURL)
    }
  }, [regionActiveFilters, statusActiveFilters])

  const tableRowClickHandler = (e: RowClickedEvent<ImportsCreateDataV5>) => {
    const { event, data, rowIndex } = e
    const target = event?.target as HTMLElement
    const rowElement = target.closest('.ag-row')

    if (
      portalContainer?.rowElement === rowElement ||
      !data?.import_errors?.length ||
      !rowElement ||
      target.tagName?.match(/WPP-(ICON-MORE|ACTION-BUTTON|ICON-BUTTON)/)
    ) {
      setPortalContainer(undefined)
    } else {
      setPortalContainer({
        rowElement,
        topPosition: (rowIndex || 0) >= importsTableReducerState.rowData.length - 5,
        errors: data.import_errors || [],
        selectedImportId: data._id || '',
      })
    }
  }

  const downloadButtonClickHandler = ({ url, file_name }: { url: URL; file_name: string }) => {
    downloadImportLogs(url)
      .then(data => {
        const downloadUrl = data.download_url
        if (!downloadUrl) return
        const a = document.createElement('a')
        a.href = downloadUrl
        a.download = file_name
        a.click()
        a.remove()
      })
      .catch(console.error)
  }

  if (
    singleProjectError?.status === 404 ||
    singleProjectError?.status === 500 ||
    (!isSingleProjectLoading && !singleProjectData && singleProjectError)
  )
    return <NotFound404Page />

  return (
    <div className="dark-bg-page">
      <section className={clsx('light-header', styles.header)}>
        <div className={styles.nav}>
          <WppBreadcrumb items={breadCrumbItems} middleTruncation onWppChange={handleRouteChange} />
          <Banner
            shouldBannerShow={!!toastDetails}
            text={toastDetails?.message}
            type={toastDetails?.type}
            onClose={() => setToastDetails(undefined)}
          />
        </div>

        <PageHeader
          className={styles.space}
          isLoading={isInitialising}
          tags={[]}
          createdDetails=""
          pageTitle="Import log"
          details={{
            Project: project_name!,
            Client: `${clientDetails?.client_name || client_id} | ${brandDetails?.brand_name || brand_id}`,
          }}
        />
      </section>

      <section className="page-content">
        <div>
          <Filters
            filtersId="imports-summary"
            isTargetApiLoading={isImportLogsLoading}
            initialisation={{
              skeletonCount: 2,
              isInitialising: isTableFirstLoad,
            }}
            reducerState={filtersReducerState}
            reducerDispatch={filtersReducerDispatch}
            isItNotGrowLast
          />
        </div>
        <TableMetaDataRow
          text={`Showing ${importsTableReducerState.itemCount} results`}
          refreshHandler={() =>
            refreshHandler(setShouldAppend, importsTableReducerDispatch, importLogsUrl, setImportLogsUrl)
          }
          isInitialising={isTableFirstLoad}
        />

        <Table
          theme="tertiary"
          columnDefs={columnStructure(
            portalContainer?.selectedImportId,
            portalContainer?.topPosition,
            downloadButtonClickHandler,
          )}
          loadedRows={importsTableReducerState.itemCount}
          totalRows={importsTableReducerState.totalItemCount}
          rowData={importsTableReducerState.rowData}
          isInitialising={isTableFirstLoad}
          loading={isImportLogsLoading}
          isDrillable
          isOutsideTableBoundary
          onRowClicked={tableRowClickHandler}
          onLoadMore={() =>
            handleLoadMore(
              importLogsData,
              setShouldAppend,
              IMPORT_LOGS_API_ITEM_LIMITS,
              importsTableReducerDispatch,
              importLogsUrl,
              setImportLogsUrl,
            )
          }
          onSortingUpdate={sortData =>
            handleSortingUpdate(sortData, setShouldAppend, importLogsUrl, importsTableReducerDispatch, setImportLogsUrl)
          }
          noRowsOverlayComponent={() => (
            <WppTypography tag="p" type="s-body">
              No import logs available
            </WppTypography>
          )}
        />
      </section>

      <ErrorPortal container={portalContainer?.rowElement}>
        <div
          className={clsx(styles.errorContainer, styles.show, {
            [styles.topPosition]: portalContainer?.topPosition,
          })}
        >
          <div className={styles.validationErrors}>
            {portalContainer?.errors.map((error, index) => (
              <WppTypography key={index} type="xs-body" className={styles.errorText}>
                {error}
              </WppTypography>
            ))}
          </div>
        </div>
      </ErrorPortal>
    </div>
  )
}
